import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20PageResponse } from '@/shared/api/normalize-pro20-page';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';
import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  CreateEventDateParams,
  CreateEventParams,
  DeleteEventDateParams,
  DeleteEventParams,
  EventCalendarParams,
  EventDateRange,
  EventItem,
  EventListParams,
  EventsCalendarMap,
  GetEventByIdParams,
  GetEventDateParams,
  RemoveEventImageParams,
  UpdateEventDateParams,
  UpdateEventParams,
  UpdateEventStatusParams,
  UploadEventImageParams,
  UploadEventImageResponse,
} from '../types/event.types';
import {
  normalizeEvent,
  normalizeEventDate,
  normalizeEventsCalendar,
  normalizeUploadEventImageResponse,
} from '../utils/normalize-events';

function buildEventsListUrl({
  branchCode,
  page = 0,
  size = 20,
  search,
  sortBy = 'startDate',
  sortDirection = 'desc',
  filterDate,
}: EventListParams): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  params.set('sortBy', sortBy);
  params.set('sortDirection', sortDirection);
  params.set('search', search?.trim() ?? '');
  if (filterDate?.trim()) {
    params.set('filterDate', filterDate.trim());
  }
  return `/branches/${branchCode}/events?${params.toString()}`;
}

function requireEvent(raw: unknown, context: string): EventItem {
  const normalized = normalizeEvent(raw);
  if (!normalized) {
    throw new Error(`Invalid ${context} response`);
  }
  return normalized;
}

function requireEventDate(raw: unknown, context: string): EventDateRange {
  const normalized = normalizeEventDate(raw);
  if (!normalized) {
    throw new Error(`Invalid ${context} response`);
  }
  return normalized;
}

export const eventsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEventsByBranch: builder.query<
      PaginatedResponse<EventItem>,
      EventListParams
    >({
      query: (args) => ({
        url: buildEventsListUrl(args),
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>, _meta, arg) =>
        normalizePro20PageResponse(
          normalizePro20Response(response),
          normalizeEvent,
          arg.size ?? 20,
        ),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Events, id: branchCode },
      ],
    }),

    getEventById: builder.query<EventItem, GetEventByIdParams>({
      query: ({ branchCode, eventId }) => ({
        url: `/branches/${branchCode}/events/${String(eventId)}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireEvent(normalizePro20Response(response), 'get event'),
      providesTags: (_result, _error, { branchCode, eventId }) => [
        { type: API_TAGS.Events, id: `${branchCode}-${String(eventId)}` },
      ],
    }),

    getEventsCalendar: builder.query<EventsCalendarMap, EventCalendarParams>({
      query: ({ branchCode, startDate, endDate, formCode }) => {
        const params = new URLSearchParams();
        params.set('startDate', startDate);
        params.set('endDate', endDate);
        params.set('formCode', formCode);
        return {
          url: `/branches/${branchCode}/events/calendar?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeEventsCalendar(normalizePro20Response(response)),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Events, id: branchCode },
      ],
    }),

    createEvent: builder.mutation<EventItem, CreateEventParams>({
      query: ({ branchCode, body }) => ({
        url: `/branches/${branchCode}/events`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireEvent(normalizePro20Response(response), 'create event'),
      invalidatesTags: (_result, _error, { branchCode, skipListInvalidation }) =>
        skipListInvalidation
          ? []
          : [{ type: API_TAGS.Events, id: branchCode }],
    }),

    updateEvent: builder.mutation<EventItem, UpdateEventParams>({
      query: ({ branchCode, eventId, body }) => ({
        url: `/branches/${branchCode}/events/${String(eventId)}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireEvent(normalizePro20Response(response), 'update event'),
      invalidatesTags: (
        _result,
        _error,
        { branchCode, eventId, skipListInvalidation },
      ) => {
        const detailTag = {
          type: API_TAGS.Events,
          id: `${branchCode}-${String(eventId)}`,
        } as const;
        if (skipListInvalidation) {
          return [detailTag];
        }
        return [{ type: API_TAGS.Events, id: branchCode }, detailTag];
      },
    }),

    updateEventStatus: builder.mutation<EventItem, UpdateEventStatusParams>({
      query: ({ branchCode, eventId, body }) => ({
        url: `/branches/${branchCode}/events/${String(eventId)}/status`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireEvent(normalizePro20Response(response), 'update event status'),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Events, id: branchCode },
      ],
    }),

    deleteEvent: builder.mutation<null, DeleteEventParams>({
      query: ({ branchCode, eventId }) => ({
        url: `/branches/${branchCode}/events/${String(eventId)}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Events, id: branchCode },
      ],
    }),

    duplicateEvent: builder.mutation<EventItem, DeleteEventParams>({
      query: ({ branchCode, eventId }) => ({
        url: `/branches/${branchCode}/events/${String(eventId)}/duplicate`,
        method: 'PUT',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireEvent(normalizePro20Response(response), 'duplicate event'),
      invalidatesTags: (_result, _error, { branchCode }) =>
        branchCode ? [{ type: API_TAGS.Events, id: branchCode }] : [],
    }),

    getEventDate: builder.query<EventDateRange, GetEventDateParams>({
      query: ({ branchCode, eventId, eventDateId }) => ({
        url: `/branches/${branchCode}/events/${String(eventId)}/dates/${String(eventDateId)}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireEventDate(normalizePro20Response(response), 'get event date'),
    }),

    createEventDate: builder.mutation<EventItem, CreateEventDateParams>({
      query: ({ branchCode, eventId, body }) => ({
        url: `/branches/${branchCode}/events/${String(eventId)}/dates`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireEvent(normalizePro20Response(response), 'create event date'),
      invalidatesTags: (_result, _error, { branchCode, eventId }) => [
        { type: API_TAGS.Events, id: `${branchCode}-${String(eventId)}` },
      ],
    }),

    updateEventDate: builder.mutation<EventItem, UpdateEventDateParams>({
      query: ({ branchCode, eventId, eventDateId, body }) => ({
        url: `/branches/${branchCode}/events/${String(eventId)}/dates/${String(eventDateId)}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireEvent(normalizePro20Response(response), 'update event date'),
      invalidatesTags: (_result, _error, { branchCode, eventId }) => [
        { type: API_TAGS.Events, id: `${branchCode}-${String(eventId)}` },
      ],
    }),

    deleteEventDate: builder.mutation<EventItem, DeleteEventDateParams>({
      query: ({ branchCode, eventId, eventDateId }) => ({
        url: `/branches/${branchCode}/events/${String(eventId)}/dates/${String(eventDateId)}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        requireEvent(normalizePro20Response(response), 'delete event date'),
      invalidatesTags: (_result, _error, { branchCode, eventId }) => [
        { type: API_TAGS.Events, id: `${branchCode}-${String(eventId)}` },
      ],
    }),

    uploadEventImage: builder.mutation<
      UploadEventImageResponse,
      UploadEventImageParams
    >({
      query: ({ branchCode, eventId, formData }) => ({
        url: `/branches/${branchCode}/event/upload/${String(eventId)}/image`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeUploadEventImageResponse(normalizePro20Response(response)),
    }),

    removeEventImage: builder.mutation<null, RemoveEventImageParams>({
      query: ({ branchCode, eventId }) => ({
        url: `/branches/${branchCode}/event/remove/${String(eventId)}/image`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
    }),
  }),
});

export const {
  useGetEventsByBranchQuery,
  useGetEventByIdQuery,
  useGetEventsCalendarQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useUpdateEventStatusMutation,
  useDeleteEventMutation,
  useDuplicateEventMutation,
  useLazyGetEventDateQuery,
  useCreateEventDateMutation,
  useUpdateEventDateMutation,
  useDeleteEventDateMutation,
  useUploadEventImageMutation,
  useRemoveEventImageMutation,
} = eventsApi;
